import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import axios from 'axios';
import { API_URL } from './services/api';
import ProtectedRoute from './components/ProtectedRoute';
import Sidebar from './components/Sidebar';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import AdminProfile from './pages/AdminProfile';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import CarsPage from './pages/CarsPage';
import BikesPage from './pages/BikesPage';
import CarModelDetails from './pages/CarModelDetails';
import CarVariantDetails from './pages/CarVariantDetails';
import BikeModelDetails from './pages/BikeModelDetails';
import BikeVariantDetails from './pages/BikeVariantDetails';
import DrivingSchools from './pages/DrivingSchools';
import RTOOffices from './pages/RTOOffices';
import QuestionBank from './pages/QuestionBank';
import PracticeQuestions from './pages/PracticeQuestions';
import ExamQuestions from './pages/ExamQuestions';
import TrafficSignals from './pages/TrafficSignals';
import AdManagement from './pages/AdManagement';
import ScreenManagement from './pages/ScreenManagement';
import ContentModules from './pages/ContentModules';
import ExternalLinks from './pages/ExternalLinks';
import PromotionBanners from './pages/PromotionBanners';
import InsurancePartnerLinks from './pages/InsurancePartnerLinks';
import ExclusiveServices from './pages/ExclusiveServices';
import SocialLinks from './pages/SocialLinks';
import TrafficRules from './pages/TrafficRules';
import DrivingTips from './pages/DrivingTips';
import Blogs from './pages/Blogs';
import Testimonials from './pages/Testimonials';
import FAQs from './pages/FAQs';
import Valuation from './pages/Valuation';
import ContactSubmissions from './pages/ContactSubmissions';
import PaymentDialogs from './pages/PaymentDialogs';
import PaymentKeys from './pages/PaymentKeys';
import VehicleInfo from './pages/VehicleInfo';
import Cities from './pages/Cities';
import Applications from './pages/Applications';
import ApplicationDetail from './pages/ApplicationDetail';

function NavBar({ sidebarPinned }) {
  const { isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const [systemStats, setSystemStats] = useState({ cpu: { usage: 0 }, memory: { usage: 0 } });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API_URL}/system-stats`);
        setSystemStats(response.data);
      } catch (error) {
        console.error('Failed to fetch system stats:', error);
      }
    };

    if (isAuthenticated) {
      fetchStats();
      const interval = setInterval(fetchStats, 5000); // Update every 5 seconds
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const authPages = ['/login', '/forgot-password'];
  const isAuthPage = authPages.includes(location.pathname) || location.pathname.startsWith('/reset-password');

  if (isAuthPage) return null;

  const getCpuColor = (usage) => {
    if (usage < 50) return '#10b981';
    if (usage < 80) return '#f59e0b';
    return '#ef4444';
  };

  const getMemoryColor = (usage) => {
    if (usage < 60) return '#10b981';
    if (usage < 85) return '#f59e0b';
    return '#ef4444';
  };

  return (
    <nav style={{
      position: 'fixed',
      top: 0,
      left: isAuthenticated ? (sidebarPinned ? '240px' : '72px') : 0,
      right: 0,
      height: '64px',
      backgroundColor: '#ffffff',
      borderBottom: '1px solid #e2e8f0',
      zIndex: 40,
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      transition: 'left 0.22s cubic-bezier(0.4, 0, 0.2, 1)',
    }}>
      <div style={{
        height: '100%',
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
      }}>
        {/* Logo for unauthenticated users only (shown on login pages) */}
        {!isAuthenticated && (
          <div style={{ marginRight: 'auto', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '36px', height: '36px',
              background: 'linear-gradient(135deg, #4f46e5, #4338ca)',
              borderRadius: '8px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 2px 4px rgba(79, 70, 229,0.3)',
            }}>
              <svg width="20" height="20" fill="none" stroke="white" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#4f46e5' }}>VehicleSetu</span>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>

        {isAuthenticated && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {/* System Stats */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              padding: '8px 16px',
              backgroundColor: '#f1f5f9',
              borderRadius: '12px'
            }}>
              {/* CPU Usage */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2">
                  <rect x="4" y="4" width="16" height="16" rx="2" />
                  <rect x="9" y="9" width="6" height="6" />
                  <line x1="9" y1="1" x2="9" y2="4" />
                  <line x1="15" y1="1" x2="15" y2="4" />
                  <line x1="9" y1="20" x2="9" y2="23" />
                  <line x1="15" y1="20" x2="15" y2="23" />
                  <line x1="20" y1="9" x2="23" y2="9" />
                  <line x1="20" y1="14" x2="23" y2="14" />
                  <line x1="1" y1="9" x2="4" y2="9" />
                  <line x1="1" y1="14" x2="4" y2="14" />
                </svg>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ fontSize: '10px', color: '#64748b', fontWeight: '500' }}>CPU</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{
                      width: '50px',
                      height: '6px',
                      backgroundColor: '#e2e8f0',
                      borderRadius: '3px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${systemStats.cpu.usage}%`,
                        height: '100%',
                        backgroundColor: getCpuColor(systemStats.cpu.usage),
                        borderRadius: '3px',
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                    <span style={{
                      fontSize: '12px',
                      fontWeight: '600',
                      color: getCpuColor(systemStats.cpu.usage),
                      minWidth: '32px'
                    }}>{systemStats.cpu.usage}%</span>
                  </div>
                </div>
              </div>

              {/* Memory Usage */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2">
                  <rect x="2" y="6" width="20" height="12" rx="2" />
                  <line x1="6" y1="10" x2="6" y2="14" />
                  <line x1="10" y1="10" x2="10" y2="14" />
                  <line x1="14" y1="10" x2="14" y2="14" />
                  <line x1="18" y1="10" x2="18" y2="14" />
                </svg>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ fontSize: '10px', color: '#64748b', fontWeight: '500' }}>RAM</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{
                      width: '50px',
                      height: '6px',
                      backgroundColor: '#e2e8f0',
                      borderRadius: '3px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${systemStats.memory.usage}%`,
                        height: '100%',
                        backgroundColor: getMemoryColor(systemStats.memory.usage),
                        borderRadius: '3px',
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                    <span style={{
                      fontSize: '12px',
                      fontWeight: '600',
                      color: getMemoryColor(systemStats.memory.usage),
                      minWidth: '32px'
                    }}>{systemStats.memory.usage}%</span>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={logout}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#475569',
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Logout
            </button>
          </div>
        )}
        </div>
      </div>
    </nav>
  );
}

function AppLayout({ children, sidebarPinned, onHoverChange, onTogglePin }) {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  const authPages = ['/login', '/forgot-password'];
  const isAuthPage = authPages.includes(location.pathname) || location.pathname.startsWith('/reset-password');

  if (isAuthPage) {
    return <main style={{ minHeight: '100vh' }}>{children}</main>;
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', paddingTop: '64px' }}>
      {isAuthenticated && <Sidebar pinned={sidebarPinned} onHoverChange={onHoverChange} onTogglePin={onTogglePin} />}
      <main style={{
        flex: 1,
        padding: '16px',
        backgroundColor: '#f8fafc',
        minHeight: 'calc(100vh - 64px)',
        marginLeft: isAuthenticated ? (sidebarPinned ? '240px' : '72px') : 0,
        transition: 'margin-left 0.2s ease'
      }}>
        {children}
      </main>
    </div>
  );
}

function App() {
  const [sidebarPinned, setSidebarPinned] = useState(() => {
    try { return localStorage.getItem('sidebarPinned') === 'true'; } catch { return false; }
  });
  const [, setSidebarHovered] = useState(false);

  const togglePin = () => {
    setSidebarPinned((prev) => {
      const next = !prev;
      try { localStorage.setItem('sidebarPinned', String(next)); } catch { /* ignore */ }
      return next;
    });
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      <NavBar sidebarPinned={sidebarPinned} />
      <AppLayout sidebarPinned={sidebarPinned} onHoverChange={setSidebarHovered} onTogglePin={togglePin}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />

          {/* Cars - Tabbed Page */}
          <Route path="/cars" element={<ProtectedRoute><CarsPage /></ProtectedRoute>} />
          <Route path="/car-models/:id" element={<ProtectedRoute><CarModelDetails /></ProtectedRoute>} />
          <Route path="/car-variants/:id" element={<ProtectedRoute><CarVariantDetails /></ProtectedRoute>} />
          <Route path="/admin-profile" element={<ProtectedRoute><AdminProfile /></ProtectedRoute>} />

          {/* Bikes - Tabbed Page */}
          <Route path="/bikes" element={<ProtectedRoute><BikesPage /></ProtectedRoute>} />
          <Route path="/bike-models/:id" element={<ProtectedRoute><BikeModelDetails /></ProtectedRoute>} />
          <Route path="/bike-variants/:id" element={<ProtectedRoute><BikeVariantDetails /></ProtectedRoute>} />

          {/* Services */}
          <Route path="/driving-schools" element={<ProtectedRoute><DrivingSchools /></ProtectedRoute>} />
          <Route path="/rto-offices" element={<ProtectedRoute><RTOOffices /></ProtectedRoute>} />

          {/* Knowledge Base */}
          <Route path="/question-bank" element={<ProtectedRoute><QuestionBank /></ProtectedRoute>} />
          <Route path="/practice-questions" element={<ProtectedRoute><PracticeQuestions /></ProtectedRoute>} />
          <Route path="/exam-questions" element={<ProtectedRoute><ExamQuestions /></ProtectedRoute>} />
          <Route path="/traffic-signals" element={<ProtectedRoute><TrafficSignals /></ProtectedRoute>} />

          {/* Ad Management */}
          <Route path="/ad-management" element={<ProtectedRoute><AdManagement /></ProtectedRoute>} />

          {/* Screen Management */}
          <Route path="/screen-management" element={<ProtectedRoute><ScreenManagement /></ProtectedRoute>} />

          {/* Content Modules */}
          <Route path="/content-modules" element={<ProtectedRoute><ContentModules /></ProtectedRoute>} />

          {/* External Links */}
          <Route path="/external-links" element={<ProtectedRoute><ExternalLinks /></ProtectedRoute>} />
          <Route path="/promotion-banners" element={<ProtectedRoute><PromotionBanners /></ProtectedRoute>} />
          <Route path="/insurance-partner-links" element={<ProtectedRoute><InsurancePartnerLinks /></ProtectedRoute>} />
          <Route path="/exclusive-services" element={<ProtectedRoute><ExclusiveServices /></ProtectedRoute>} />
          <Route path="/social-links" element={<ProtectedRoute><SocialLinks /></ProtectedRoute>} />
          <Route path="/traffic-rules" element={<ProtectedRoute><TrafficRules /></ProtectedRoute>} />
          <Route path="/driving-tips" element={<ProtectedRoute><DrivingTips /></ProtectedRoute>} />

          {/* Blogs */}
          <Route path="/blogs" element={<ProtectedRoute><Blogs /></ProtectedRoute>} />

          {/* Testimonials */}
          <Route path="/testimonials" element={<ProtectedRoute><Testimonials /></ProtectedRoute>} />

          {/* FAQs */}
          <Route path="/faqs" element={<ProtectedRoute><FAQs /></ProtectedRoute>} />

          {/* Contact Submissions */}
          <Route path="/contact-submissions" element={<ProtectedRoute><ContactSubmissions /></ProtectedRoute>} />

          {/* Payment Dialogs */}
          <Route path="/payment-dialogs" element={<ProtectedRoute><PaymentDialogs /></ProtectedRoute>} />

          {/* Payment Keys */}
          <Route path="/payment-keys" element={<ProtectedRoute><PaymentKeys /></ProtectedRoute>} />

          {/* Vehicle Info */}
          <Route path="/vehicle-info" element={<ProtectedRoute><VehicleInfo /></ProtectedRoute>} />

          {/* Cities */}
          <Route path="/cities" element={<ProtectedRoute><Cities /></ProtectedRoute>} />

          {/* Applications (ad container) */}
          <Route path="/applications" element={<ProtectedRoute><Applications /></ProtectedRoute>} />
          <Route path="/applications/:id" element={<ProtectedRoute><ApplicationDetail /></ProtectedRoute>} />

          {/* Valuation */}
          <Route path="/valuation" element={<ProtectedRoute><Valuation /></ProtectedRoute>} />

          {/* Redirects for old routes */}
          <Route path="/brands" element={<Navigate to="/cars?tab=brands" replace />} />
          <Route path="/car-models" element={<Navigate to="/cars?tab=models" replace />} />
          <Route path="/car-variants" element={<Navigate to="/cars?tab=variants" replace />} />
          <Route path="/car-service-centers" element={<Navigate to="/cars?tab=service-centers" replace />} />
          <Route path="/car-dealers" element={<Navigate to="/cars?tab=dealers" replace />} />
          <Route path="/bike-brands" element={<Navigate to="/bikes?tab=brands" replace />} />
          <Route path="/bike-models" element={<Navigate to="/bikes?tab=models" replace />} />
          <Route path="/bike-variants" element={<Navigate to="/bikes?tab=variants" replace />} />
          <Route path="/bike-service-centers" element={<Navigate to="/bikes?tab=service-centers" replace />} />
          <Route path="/bike-dealers" element={<Navigate to="/bikes?tab=dealers" replace />} />

          <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </AppLayout>
    </div>
  );
}

export default App;
